import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI, followsAPI, formatApiError, resolveImageUrl, investmentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, PaperPlaneTilt, Storefront, ArrowRight, UserPlus, UserMinus, Users, ChartLineUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { stripHtmlToText } from '../utils/text';
import { profileLink } from '../utils/profile';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function UserProfilePage() {
  const { userId: legacyUserId, username } = useParams();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [preferredVendors, setPreferredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [userInvestments, setUserInvestments] = useState([]);

  // The resolved profile user_id, used for follow/unfollow/investments lookups.
  // Falls back to the URL param while the profile is loading.
  const profileUserId = profileUser?.user_id || legacyUserId;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);

    const loadAll = async () => {
      try {
        // Pick API based on which route param was provided
        const profileResp = username
          ? await usersAPI.getByUsername(username)
          : await usersAPI.getProfile(legacyUserId);

        if (!mounted) return;
        setProfileUser(profileResp.data.user);
        setProducts(profileResp.data.products || []);
        setPreferredVendors(profileResp.data.preferred_vendors || []);
        setIsFollowing(profileResp.data.is_following || false);

        // Investments lookup needs user_id; use the resolved one
        const resolvedId = profileResp.data.user?.user_id;
        if (resolvedId) {
          try {
            const { data } = await investmentsAPI.getUserInvestments(resolvedId);
            if (mounted) setUserInvestments(data.investments || []);
          } catch (err) { console.error('Failed to load user investments:', err); }
        }
      } catch (error) {
        if (!mounted) return;
        // Username route: treat 404 as "not found" (no toast — could be a typo'd URL)
        if (username && error?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error(formatApiError(error));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadAll();
    return () => { mounted = false; };
  }, [legacyUserId, username]);

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to follow users');
      return;
    }
    if (!profileUserId) return;

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followsAPI.unfollow(profileUserId);
        setIsFollowing(false);
        setProfileUser(prev => ({ ...prev, followers_count: (prev.followers_count || 1) - 1 }));
        toast.success('Unfollowed');
      } else {
        await followsAPI.follow(profileUserId);
        setIsFollowing(true);
        setProfileUser(prev => ({ ...prev, followers_count: (prev.followers_count || 0) + 1 }));
        toast.success('Now following!');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setFollowLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  if (notFound || !profileUser) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center px-4">
        <p className="text-white text-lg font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>User not found</p>
        <p className="text-[#999999] text-sm mb-6 text-center">We couldn't find an account at this URL.</p>
        <Link to="/explore">
          <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Browse the marketplace</Button>
        </Link>
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === profileUser.user_id;

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/explore" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Explore
        </Link>

        {/* Profile Header */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-8" data-testid="user-profile-card">
          <CardContent className="p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {profileUser.avatar_url ? (
                <img 
                  src={profileUser.avatar_url} 
                  alt={profileUser.name}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-bold text-3xl">
                  {profileUser.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              
              <div className="flex-1">
                <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="profile-name">
                  {profileUser.name}
                </h1>
                {profileUser.username && (
                  <p className="text-[#22C55E] font-semibold">${profileUser.username}</p>
                )}
                <p className="text-[#999999]">{profileUser.email}</p>
                {profileUser.bio && (
                  <p className="mt-2 text-white">{profileUser.bio}</p>
                )}
                
                {/* Stats */}
                <div className="flex gap-6 mt-4">
                  <div>
                    <span className="text-white font-bold">{profileUser.followers_count || 0}</span>
                    <span className="text-[#999999] ml-1">Followers</span>
                  </div>
                  <div>
                    <span className="text-white font-bold">{profileUser.following_count || 0}</span>
                    <span className="text-[#999999] ml-1">Following</span>
                  </div>
                  <div>
                    <span className="text-white font-bold">{products.length}</span>
                    <span className="text-[#999999] ml-1">Products</span>
                  </div>
                </div>
              </div>

              {!isOwnProfile && (
                <div className="flex gap-3">
                  <Button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`rounded-sm ${isFollowing 
                      ? 'bg-transparent border border-[#333333] text-white hover:bg-[#333333]' 
                      : 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                    }`}
                    data-testid="follow-button"
                  >
                    {isFollowing ? (
                      <>
                        <UserMinus className="w-4 h-4 mr-2" />
                        Unfollow
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Follow
                      </>
                    )}
                  </Button>
                  {isAuthenticated && (
                    <Link to={`/send?to=${profileUser.email}`}>
                      <Button variant="outline" className="rounded-sm border-[#333333] hover:bg-[#333333] text-white" data-testid="send-money-button">
                        <PaperPlaneTilt className="w-4 h-4 mr-2" />
                        Send Money
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preferred Vendors */}
        {preferredVendors.length > 0 && (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-8">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <Users className="w-5 h-5 text-[#22C55E]" />
                Preferred Vendors
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {preferredVendors.map((vendor) => (
                  <Link
                    key={vendor.user_id}
                    to={profileLink(vendor)}
                    className="flex items-center gap-4 p-4 bg-[#111111] rounded-sm hover:bg-[#222222] transition-colors"
                  >
                    {vendor.avatar_url ? (
                      <img src={vendor.avatar_url} alt={vendor.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-bold">
                        {vendor.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white truncate">{vendor.name}</p>
                      {vendor.username && (
                        <p className="text-sm text-[#22C55E]">${vendor.username}</p>
                      )}
                      {vendor.products?.length > 0 && (
                        <p className="text-xs text-[#999999]">{vendor.products.length} products</p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#999999]" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Investments */}
        {userInvestments.length > 0 && (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-8" data-testid="user-investments-section">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <ChartLineUp className="w-5 h-5 text-[#22C55E]" />
                Investments ({userInvestments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3">
                {userInvestments.map((inv) => (
                  <Link
                    key={inv.investment_id}
                    to={`/product/${inv.product_id}`}
                    className="flex items-center gap-4 p-4 bg-[#111111] rounded-sm hover:bg-[#222222] transition-colors"
                    data-testid={`public-investment-${inv.investment_id}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#22C55E]/10">
                      <ChartLineUp className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white text-sm truncate">{inv.product_name}</p>
                      <p className="text-xs text-[#999999]">
                        by {inv.seller?.name || 'Seller'} {inv.seller?.username ? `($${inv.seller.username})` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#22C55E]">{inv.percentage}% share</p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${inv.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}>
                        {inv.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Products & Services ({products.length})
          </h2>
        </div>

        {products.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
            <CardContent className="p-12 text-center">
              <Storefront className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
              <p className="text-[#999999]">No products or services listed yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card 
                key={product.product_id}
                className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none hover:-translate-y-1 hover:border-[#22C55E] transition-all duration-200 overflow-hidden"
                data-testid={`user-product-${product.product_id}`}
              >
                {product.image_url && (
                  <div className="aspect-square bg-[#111111] overflow-hidden">
                    <img 
                      src={resolveImageUrl(product.image_url)} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }}
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <span className="text-xs bg-[#333333] px-2 py-1 rounded-sm text-[#999999]">
                    {product.category}
                  </span>
                  
                  <h3 className="font-bold text-white mt-2 mb-1 line-clamp-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
                    {product.name}
                  </h3>
                  <p className="text-sm text-[#999999] mb-3 line-clamp-2">
                    {stripHtmlToText(product.description)}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>
                      {formatCurrency(product.price)}
                    </p>
                    <Link to={`/product/${product.product_id}`}>
                      <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-xs">
                        View
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
